import React from "react";
import { Image, ScrollView, StyleSheet, View } from "react-native";
import { Text } from "native-base";
import { Helmet } from "react-native-helmet-async";

import { useAppContext } from "../Universal/API/Context-API/AppContext";
import { BackButtonBackHome } from "../Universal/Buttons/BackButtonBackHomeModified";
import { webFont } from "../Universal/API/API";
import SimpleCenteredSpinner from "../Universal/SimpleCenteredSpinner/SimpleCenteredSpinner";

interface AboutProps {
  navigation: any;
  route: any;
}

const createStyles = (scaleAll: number, isSmallVersion: boolean) =>
  StyleSheet.create({
    screen: {
      flex: 1,
      backgroundColor: "#FFFFFF",
    },
    scrollContent: {
      paddingTop: 12 * scaleAll,
      paddingBottom: 72 * scaleAll,
      paddingHorizontal: 16 * scaleAll,
      alignItems: "center",
    },
    shell: {
      width: "100%",
      maxWidth: 1480 * scaleAll,
      alignSelf: "center",
    },
    hero: {
      marginTop: 18 * scaleAll,
      paddingHorizontal: 4 * scaleAll,
      marginBottom: 8 * scaleAll,
    },
    eyebrow: {
      fontFamily: webFont("Inter"),
      fontSize: 12 * scaleAll,
      color: "#6B665F",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: 0.7,
      marginBottom: 8 * scaleAll,
    },
    subtitle: {
      fontFamily: webFont("Inter"),
      fontSize: 14 * scaleAll,
      lineHeight: 21 * scaleAll,
      color: "#6F6A63",
      fontWeight: "500",
      maxWidth: 920 * scaleAll,
    },
    introCard: {
      borderRadius: 30 * scaleAll,
      backgroundColor: "#FBFBFA",
      borderWidth: 1,
      borderColor: "#ECE8E1",
      overflow: "hidden",
      marginTop: 10 * scaleAll,
    },
    introCardDesktop: {
      flexDirection: "row",
      alignItems: "stretch",
    },
    introMedia: {
      backgroundColor: "#F4F1EB",
      overflow: "hidden",
      alignItems: "center",
      justifyContent: "center",
    },
    introMediaDesktop: {
      width: "39%",
      minHeight: 420 * scaleAll,
    },
    introMediaStack: {
      width: "100%",
      aspectRatio: 1.2,
    },
    introLogoWrap: {
      width: "100%",
      height: "100%",
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: 32 * scaleAll,
      paddingVertical: 32 * scaleAll,
    },
    introLogoImage: {
      width: isSmallVersion ? 220 * scaleAll : 320 * scaleAll,
      height: isSmallVersion ? 72 * scaleAll : 104 * scaleAll,
      maxWidth: "100%",
    },
    introBody: {
      flex: 1,
      paddingHorizontal: 22 * scaleAll,
      paddingTop: 22 * scaleAll,
      paddingBottom: 22 * scaleAll,
    },
    introLead: {
      fontFamily: webFont("Inter"),
      fontSize: 16 * scaleAll,
      lineHeight: 24 * scaleAll,
      color: "#3F3A35",
      fontWeight: "700",
      marginBottom: 16 * scaleAll,
    },
    introText: {
      fontFamily: webFont("Inter"),
      fontSize: 15 * scaleAll,
      lineHeight: 24 * scaleAll,
      color: "#3F3A35",
      fontWeight: "500",
      marginBottom: 14 * scaleAll,
    },
    introTextLast: {
      marginBottom: 0,
    },
    sectionsWrap: {
      marginTop: 18 * scaleAll,
      gap: 14 * scaleAll,
    },
    sectionCard: {
      borderRadius: 28 * scaleAll,
      backgroundColor: "#FFFFFF",
      borderWidth: 1,
      borderColor: "#ECE8E1",
      paddingHorizontal: 22 * scaleAll,
      paddingTop: 20 * scaleAll,
      paddingBottom: 20 * scaleAll,
    },
    sectionTitle: {
      fontFamily: webFont("Inter"),
      fontSize: 18 * scaleAll,
      lineHeight: 22 * scaleAll,
      color: "#111111",
      fontWeight: "700",
      marginBottom: 12 * scaleAll,
    },
    sectionText: {
      fontFamily: webFont("Inter"),
      fontSize: 15 * scaleAll,
      lineHeight: 24 * scaleAll,
      color: "#4A443D",
      fontWeight: "500",
      marginBottom: 12 * scaleAll,
    },
    sectionTextLast: {
      marginBottom: 0,
    },
    loadingWrap: {
      minHeight: 320 * scaleAll,
      alignItems: "center",
      justifyContent: "center",
    },
  });

const About: React.FC<AboutProps> = ({ navigation }) => {
  const { isSmallVersion, isMediumVersion, scaleAll, aboutData } = useAppContext();
  const styles = React.useMemo(
    () => createStyles(scaleAll, isSmallVersion),
    [isSmallVersion, scaleAll]
  );

  const sections = aboutData?.about_big_sections ?? [];
  const introParagraphs = aboutData?.about_text_small ?? [];
  const useWideLayout = !isSmallVersion && !isMediumVersion;

  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>О компании | Deante</title>
        <meta property="og:title" content="О компании | Deante" />
        <meta
          name="description"
          content="Deante — бренд керамической плитки и керамогранита премиум-класса. Узнайте больше о философии, производстве и подходе к дизайну."
        />
        <meta
          property="og:description"
          content="Фабрика Deante — сочетание инновационных технологий и авторского подхода в производстве плитки. Мы создаём материалы, которые вдохновляют."
        />
        <meta property="og:url" content="https://deante.pro/about" />
        <meta name="twitter:title" content="О фабрике Deante" />
        <meta
          name="twitter:description"
          content="Философия Deante, производство, подход к дизайну. Узнайте, кто мы."
        />
        <meta
          name="twitter:image"
          content="https://deante.pro/js/logo/about-cover.jpg"
        />
      </Helmet>

      <View style={styles.screen}>
        <BackButtonBackHome navigation={navigation} />

        <ScrollView contentContainerStyle={styles.scrollContent}>
          <View style={styles.shell}>
            <View style={styles.hero}>
              <Text selectable={false} style={styles.eyebrow}>
                О компании
              </Text>
            </View>

            {!aboutData ? (
              <View style={styles.loadingWrap}>
                <SimpleCenteredSpinner scaleAll={scaleAll} height={20} width={20} />
              </View>
            ) : (
              <>
                <View
                  style={[
                    styles.introCard,
                    useWideLayout ? styles.introCardDesktop : null,
                  ]}
                >
                  <View
                    style={[
                      styles.introMedia,
                      useWideLayout ? styles.introMediaDesktop : styles.introMediaStack,
                    ]}
                  >
                    <View style={styles.introLogoWrap}>
                      <Image
                        style={styles.introLogoImage}
                        source={require("./../../assets/Images/logo_deante_big.png")}
                        resizeMode="contain"
                      />
                    </View>
                  </View>

                  <View style={styles.introBody}>
                    <Text selectable={false} style={styles.introLead}>
                      Дизайн, технология и функциональность в одном бренде.
                    </Text>

                    {introParagraphs.map((paragraph, index) => (
                      <Text
                        key={`intro-${index}`}
                        selectable={false}
                        style={[
                          styles.introText,
                          index === introParagraphs.length - 1 ? styles.introTextLast : null,
                        ]}
                      >
                        {paragraph}
                      </Text>
                    ))}
                  </View>
                </View>

                <View style={styles.sectionsWrap}>
                  {sections.map((section, index) => (
                    <View key={`${section?.title ?? "section"}-${index}`} style={styles.sectionCard}>
                      <Text selectable={false} style={styles.sectionTitle}>
                        {section.title}
                      </Text>

                      {(section.content ?? []).map((paragraph: string, paragraphIndex: number) => (
                        <Text
                          key={`${section.title}-${paragraphIndex}`}
                          selectable={false}
                          style={[
                            styles.sectionText,
                            paragraphIndex === section.content.length - 1
                              ? styles.sectionTextLast
                              : null,
                          ]}
                        >
                          {paragraph}
                        </Text>
                      ))}
                    </View>
                  ))}
                </View>
              </>
            )}
          </View>
        </ScrollView>
      </View>
    </>
  );
};

export default React.memo(About);
